import React from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Calendar } from "lucide-react";

/**
 * EventMiniMap — mini-mappa Leaflet riutilizzabile per B2B Fairs / B2B Events.
 * Props:
 *   - events: array di eventi con {id, name, date, location, sector, lat, lng, host_company?, visibility?}
 *   - accent: colore hex del marker (default cyan #0891B2)
 *   - icon: React icon component da mostrare nell'header
 *   - title: titolo card
 *   - maxItems: quanti eventi mostrare (default 12)
 */
export default function EventMiniMap({
  events = [],
  accent = "#0891B2",
  Icon = MapPin,
  title = "Mappa Eventi",
  maxItems = 12,
  onEventClick = null,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const future = events
    .filter(e => e.date && e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  const upcoming = (future.length > 0 ? future : [...events].sort((a, b) => (b.date || "").localeCompare(a.date || "")))
    .slice(0, maxItems);
  const withCoords = upcoming.filter(e => e.lat && e.lng);

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04] flex flex-col overflow-hidden" style={{ height: 520 }}>
      <div className="flex items-center gap-2 px-5 pt-4 pb-3 border-b border-white/[0.04] flex-shrink-0">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <p className="text-sm text-white font-semibold tracking-[0.12em] uppercase">{title}</p>
        <span className="ml-auto text-[10px] text-[#A0AEC0]">{upcoming.length} entries</span>
      </div>
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Map */}
        <div className="flex-1">
          <MapContainer center={[48, 10]} zoom={4} style={{ width: "100%", height: "100%" }} zoomControl={false} scrollWheelZoom={false}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution="" />
            {withCoords.map((ev) => (
              <CircleMarker
                key={ev.id || `${ev.name}-${ev.date}`}
                center={[ev.lat, ev.lng]}
                radius={8}
                pathOptions={{ fillColor: accent, fillOpacity: 0.85, color: accent, weight: 1.5 }}
                eventHandlers={onEventClick ? { click: () => onEventClick(ev) } : undefined}
              >
                <LeafletTooltip opacity={1}>
                  <div className="bg-[#0D0F12] border border-white/[0.04] p-2 text-xs min-w-[160px]">
                    <p className="text-white font-semibold">{ev.name}</p>
                    <p style={{ color: accent }}>{ev.date}</p>
                    <p className="text-[#CBD5E1]">{ev.location}</p>
                    {ev.host_company && <p className="text-[#A0AEC0]">Host: {ev.host_company}</p>}
                    {ev.visibility && <p className="text-[#A0AEC0]">{ev.visibility}</p>}
                    {ev.sector && <p className="text-[#A0AEC0]">{ev.sector}</p>}
                    {onEventClick && <p className="text-white mt-1 italic">Clicca per aprire la scheda</p>}
                  </div>
                </LeafletTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        {/* Event list */}
        <div className="w-48 border-l border-white/[0.04] overflow-y-auto flex-shrink-0 bg-[#0A0C10]">
          {upcoming.length === 0 ? (
            <p className="text-[10px] text-[#A0AEC0] p-3">Nessun evento pianificato</p>
          ) : upcoming.map((ev) => (
            <div
              key={ev.id || `${ev.name}-${ev.date}`}
              data-testid="mini-map-event-item"
              onClick={onEventClick ? () => onEventClick(ev) : undefined}
              className={`px-3 py-2 border-b border-white/[0.04] transition-colors ${onEventClick ? "hover:bg-[#1A1D24] cursor-pointer" : ""}`}
            >
              <div className="flex items-start gap-1.5">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                <div className="min-w-0">
                  <p className="text-[10px] text-white font-medium leading-tight truncate">{ev.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Calendar className="w-2.5 h-2.5 text-[#A0AEC0]" />
                    <span className="text-[9px] text-[#A0AEC0]">{ev.date}</span>
                  </div>
                  <p className="text-[9px] text-[#A0AEC0] truncate">{ev.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
